import type { TFunction } from 'i18next'

import { formatDate, formatMoney, formatNumber } from '../../lib/format'
import type { BrokerOption, CarrierOption, RateConfirmationStatus, ReadingFlag } from './api'

/** The chip each status wears: in progress, read, failed — said in words too, never by colour alone. */
export function statusTone(status: RateConfirmationStatus): 'pending' | 'active' | 'blocked' {
  switch (status) {
    case 'Reading':
      return 'pending'
    case 'ReadyForReview':
      return 'active'
    case 'ReadFailed':
      return 'blocked'
  }
}

/**
 * An amount in the currency the document states. A code Intl does not take — "US$", a word —
 * reads in dollars rather than failing the page: the currency check flags anything that is
 * not USD, and the figure must still show.
 */
export function money(amount: number, currency: string | null, language: string): string {
  const code = currency?.trim().toUpperCase()

  if (code !== undefined && code !== '' && code !== 'USD') {
    try {
      return formatMoney(amount, code, language)
    } catch {
      // Not a currency Intl knows: dollars, with the flag beside it saying what was printed.
    }
  }

  return formatMoney(amount, 'USD', language)
}

/**
 * A day as the reading gives it — YYYY-MM-DD — in the reader's words. Text that is not one is
 * shown as it was read (its flag says so): formatting it would fail, and hiding it would hide
 * the very thing to check. "Not one" includes a calendar day that does not exist: a browser
 * rolls 2026-02-30 over to March 2, which would show a different day from the one read
 * (Copilot on #66), so the day must come back from the calendar exactly as it went in.
 */
export function dayText(raw: string, language: string): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw)
  const [year, month, day] = parts === null ? [] : parts.slice(1).map(Number)

  if (year === undefined || month === undefined || day === undefined) {
    return raw
  }

  // Date.UTC reads a year under 100 as 19xx — year 0 included — so the round trip refuses
  // those too, with no rule of their own.
  const calendar = new Date(Date.UTC(year, month - 1, day))
  const exact =
    calendar.getUTCFullYear() === year && calendar.getUTCMonth() === month - 1 && calendar.getUTCDate() === day

  return exact ? formatDate(raw, language) : raw
}

/**
 * What kind of value each flag's values are, so a sentence shows them as the reader expects.
 * `money` is the document's own figure, in the currency it printed — the platform adds them up
 * without converting anything (Copilot on #66); `usd` is the platform's own rule, a load's
 * ceiling, which is dollars whatever the document says.
 */
const VALUE_FORMATS: Readonly<Record<string, Readonly<Record<string, 'money' | 'usd' | 'date' | 'number'>>>> = {
  total_does_not_add_up: { lineHaul: 'money', charges: 'money', sum: 'money', total: 'money' },
  amount_out_of_range: { amount: 'money', max: 'usd' },
  stop_before_previous: { date: 'date', previous: 'date' },
  stop_window_reversed: { from: 'date', to: 'date' },
  stop_date_past: { date: 'date', today: 'date' },
  weight_out_of_range: { pounds: 'number', max: 'number' },
}

/** The codes whose sentence names something the document may not have printed. */
const UNNAMED_WITHOUT: Readonly<Record<string, string>> = {
  equipment_not_carried: 'printed',
  carrier_not_in_book: 'name',
}

/**
 * A flag in the reader's language. The values arrive as the server keeps them — invariant
 * numbers, ISO days — and are formatted here, the document's amounts in `currency`, the
 * currency it printed; a code this build does not know reads as a translated generic, never
 * as the code itself.
 */
export function flagText(flag: ReadingFlag, t: TFunction, language: string, currency: string | null): string {
  const formats = VALUE_FORMATS[flag.code] ?? {}
  const values: Record<string, string> = {}

  for (const [key, raw] of Object.entries(flag.values)) {
    const format = formats[key]
    const figure = Number(raw)

    values[key] =
      format === 'money' && Number.isFinite(figure)
        ? money(figure, currency, language)
        : format === 'usd' && Number.isFinite(figure)
          ? formatMoney(figure, 'USD', language)
        : format === 'number' && Number.isFinite(figure)
          ? formatNumber(figure, language)
          : format === 'date'
            ? dayText(raw, language)
            : raw
  }

  const unnamed = UNNAMED_WITHOUT[flag.code]
  const key = `rateConfirmations.flags.${flag.code}${unnamed !== undefined && values[unnamed] === undefined ? '_unnamed' : ''}`
  const text = t(key, values)

  // i18next echoes the key back when it has no translation for it.
  return text === key ? t('rateConfirmations.flags.unknown') : text
}

/**
 * Where on the reading a flag points, in words — for the list of everything to check, where
 * the sentence stands away from its field. Null for a path this build does not name.
 */
export function flagWhere(flag: ReadingFlag, t: TFunction): string | null {
  const field = flag.field
  if (field === null) {
    return null
  }

  const charge = /^money\.accessorials\[(\d+)\]/.exec(field)
  if (charge !== null) {
    return t('rateConfirmations.where.charge', { number: charge[1] })
  }

  const stop = /^stops\[(\d+)\]\.(\w+)$/.exec(field)
  if (stop !== null) {
    const part = STOP_PARTS[stop[2] ?? '']
    return part === undefined ? null : t(`rateConfirmations.where.${part}`, { number: stop[1] })
  }

  const fixed = FIXED_PLACES[field]
  return fixed === undefined ? null : t(`rateConfirmations.where.${fixed}`)
}

const STOP_PARTS: Readonly<Record<string, string>> = {
  date: 'stopDate',
  last_date: 'stopDate',
  time_from: 'stopTime',
  time_to: 'stopTime',
  state: 'stopPlace',
  city: 'stopPlace',
}

const FIXED_PLACES: Readonly<Record<string, string>> = {
  'money.total': 'total',
  'money.line_haul': 'lineHaul',
  'money.currency': 'currency',
  stops: 'stops',
  'broker.mc_number': 'brokerMc',
  'broker.usdot_number': 'brokerUsdot',
  'carrier.mc_number': 'carrierMc',
  'carrier.usdot_number': 'carrierUsdot',
  'carrier.name': 'carrier',
  'equipment.types': 'equipment',
  'freight.weight_pounds': 'weight',
}

/** What pointed at a broker of the book: its number, a contact, its domain or its name. */
export function brokerFoundBy(option: BrokerOption, t: TFunction): string {
  return t(`rateConfirmations.review.brokerFoundBy.${option.foundBy}`, {
    evidence: option.evidence ?? '',
    agent: option.agentName ?? '',
    defaultValue: t('rateConfirmations.review.foundByOther'),
  })
}

/** What pointed at a carrier of the book. */
export function carrierFoundBy(option: CarrierOption, t: TFunction): string {
  const evidence = option.foundBy === 'Mc' ? option.mcNumber : option.foundBy === 'Usdot' ? option.usdotNumber : null
  return t(`rateConfirmations.review.carrierFoundBy.${option.foundBy}`, {
    evidence: evidence ?? '',
    defaultValue: t('rateConfirmations.review.foundByOther'),
  })
}

/** The flags of one document, by the field each points at — where the reading shows them. */
export type FlagsByField = ReadonlyMap<string, readonly ReadingFlag[]>

export function flagsByField(flags: readonly ReadingFlag[]): FlagsByField {
  const byField = new Map<string, ReadingFlag[]>()
  for (const flag of flags) {
    if (flag.field !== null) {
      byField.set(flag.field, [...(byField.get(flag.field) ?? []), flag])
    }
  }
  return byField
}
